import { ref, type Ref } from 'vue'
import type { PageHeaderAction } from '@/components/layout/BasePageHeaderActions.vue'
import type { BreadcrumbItem } from '@/types/breadcrumbs'

const title: Ref<string | null> = ref(null)
const subtitle: Ref<string | null> = ref(null)
const breadcrumbs: Ref<BreadcrumbItem[] | null> = ref(null)
const actions: Ref<PageHeaderAction[]> = ref([])
const actionsFromSlot: Ref<boolean> = ref(false)
/** True once AppTopBarV2 has mounted and #topbar-page-actions exists (avoids Teleport target missing). */
const teleportTargetReady: Ref<boolean> = ref(false)

/**
 * Ownership token for PageTopBarRegistration instances.
 * Scoped to the route path at acquire time so a route change can invalidate a
 * leftover override even before the previous view finishes unmounting.
 */
let nextRegistrationId = 1
let activeRegistrationId: number | null = null
let activeRegistrationPath: string | null = null

/**
 * Composable for TopBar V2: shared title and actions state between layout (AppTopBarV2)
 * and pages (PageTopBarRegistration or direct usage).
 *
 * Lifecycle rule (DD-PAGE-HEADER-STALE-FIX): imperative title/subtitle/breadcrumb overrides
 * are owned by PageTopBarRegistration for the route path that acquired them. On unmount
 * (or route change away from that path) those fields clear so route.meta.title wins for
 * pages that do not register (e.g. D&D Queue).
 */
export function useTopBar() {
  function setTitle(value: string | null) {
    title.value = value
  }

  function setSubtitle(value: string | null) {
    subtitle.value = value
  }

  /** Prefer committing only full trails; pages use PageTopBarRegistration `breadcrumbsReady` until data resolves. */
  function setBreadcrumbs(value: BreadcrumbItem[] | null) {
    breadcrumbs.value = value && value.length ? value : null
  }

  function setActions(value: PageHeaderAction[]) {
    actions.value = Array.isArray(value) ? value : []
  }

  function setActionsFromSlot(value: boolean) {
    actionsFromSlot.value = value
  }

  function setTeleportTargetReady(value: boolean) {
    teleportTargetReady.value = value
  }

  /**
   * Clears teleported actions only. Title/subtitle/breadcrumbs are owned by
   * acquireTopBarRegistration / releaseTopBarRegistration.
   */
  function clearTopBarActions() {
    actions.value = []
    actionsFromSlot.value = false
  }

  function clearTitleAndCrumbs() {
    title.value = null
    subtitle.value = null
    breadcrumbs.value = null
  }

  /** Full reset (title, breadcrumbs, actions). */
  function clear() {
    clearTitleAndCrumbs()
    clearTopBarActions()
    activeRegistrationId = null
    activeRegistrationPath = null
  }

  /**
   * Called by PageTopBarRegistration on setup. `routePath` scopes ownership so
   * reconcileTopBarForRoute can drop overrides when navigating away.
   */
  function acquireTopBarRegistration(routePath: string): number {
    const id = nextRegistrationId++
    activeRegistrationId = id
    activeRegistrationPath = routePath
    return id
  }

  /**
   * Called by PageTopBarRegistration on unmount. If this instance is still the
   * active owner, clears title/subtitle/breadcrumbs (+ actions).
   */
  function releaseTopBarRegistration(ownerId: number) {
    clearTopBarActions()
    if (activeRegistrationId !== ownerId) return
    activeRegistrationId = null
    activeRegistrationPath = null
    clearTitleAndCrumbs()
  }

  function hasActiveTopBarRegistration(): boolean {
    return activeRegistrationId != null
  }

  /**
   * Route-change reconcile: if the active override belongs to a different path
   * (or there is none), drop chrome so AppTopBarV2 falls through to route.meta.title.
   */
  function reconcileTopBarForRoute(currentPath: string) {
    if (activeRegistrationId != null && activeRegistrationPath === currentPath) {
      return
    }
    activeRegistrationId = null
    activeRegistrationPath = null
    clearTitleAndCrumbs()
  }

  return {
    title,
    subtitle,
    breadcrumbs,
    actions,
    actionsFromSlot,
    teleportTargetReady,
    setTitle,
    setSubtitle,
    setBreadcrumbs,
    setActions,
    setActionsFromSlot,
    setTeleportTargetReady,
    clearTopBarActions,
    clearTitleAndCrumbs,
    clear,
    acquireTopBarRegistration,
    releaseTopBarRegistration,
    hasActiveTopBarRegistration,
    reconcileTopBarForRoute,
  }
}
